import React, { Fragment, useEffect, useState } from "react";
import { Intent } from "@blueprintjs/core";
import { Button, LinkButton } from "components/buttons";
import { Breadcrumbs, VerticalLinkGroup } from "components/navigation";
import { NotificationInline, NotificationToaster } from "components/notifications";
import { CircleStatus, StatusBlock } from "components/status";
import { UserAccess, UserHasAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { Navigate, Route, Routes, useNavigate, useLocation, useParams } from "react-router-dom";
import { AlertService, EmployeeService, LicenceCheckingService, LinkService } from "services";
import { EmployeeLicenceBureau } from "pages/employee/edit/licence-bureau/EmployeeLicenceBureau";
import { ShowHide } from "components/layout";
import { LicenceBureauService } from "services/LicenceBureauService";
import { DefectReporting } from "pages/walkaround-check/shared/DefectReporting";
import { CollisionListing } from "pages/collisions/shared/CollisionListing";
import { EmployeeLicenceChecking } from "pages/employee/edit/licence-checking";
import { Tooltip } from "components/tooltip";
import { LicenceCheckingManualCheckModal } from "pages/licence-checking/shared/manual-check";
import { ChecksListing } from "pages/checks";
import { Documents } from "pages/employee/documents";
import { EmployeeAssessments } from "pages/employee/edit/assessments";
import { EmployeeMedical } from "pages/employee/edit/medical";
import { EmployeeReviews } from "pages/employee/edit/reviews";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import classNames from "classnames";

function LegacyRedirect({ id }) {
    useEffect(() => {
        window.location.href = LinkService.getDriverProfileUrl(id);
    }, [id]);
    return null;
}

export function Employee(props) {
    const noLicenceCheckCode = 404;
    const requiredActions = ["Employees:View", "AuthorisedDriver"];
    const licenceBureauAction = 'LicenceCheck:LicenceBureau:View';
    const licenceCheckManualCheckAction = 'LicenceChecking:PerformCheck';
    const alertTypeKeys = [
        "licence-bureau-low",
        "licence-bureau-high",
        "licence-bureau-critical"
    ];

    const { id } = useParams();
    const location = useLocation();
    var navigate = useNavigate();
    const dispatch = useDispatch();
    const licenceCheckingCredits = useSelector((state) => state.licenceChecking.licenceCredits);
    const canAccessPage = UserHasAccess(requiredActions);
    const canAccessLicenceBureau = UserHasAccess(licenceBureauAction);
    const canPerformLicenceCheckManualChecks = UserHasAccess(licenceCheckManualCheckAction);

    const [loadingData, setLoadingData] = useState(true);
    const [employee, setEmployee] = useState(null);
    const [employeeOptions, setEmployeeOptions] = useState(null);

    const [loadingLicenceBureau, setLoadingLicenceBureau] = useState(false);
    const [licenceBureauAlertTypes, setLicenceBureauAlertTypes] = useState([]);
    const [licenceBureauAlertStatus, setLicenceBureauAlertStatus] = useState(null);
    const [licenceCheck, setLicenceCheck] = useState(null);
    const [licenceCheckError, setLicenceCheckError] = useState(null);
    const [licenceLink, setLicenceLink] = useState(null);
    const [licenceCheckDate, setLicenceCheckDate] = useState('never');

    const [showLicenceCheckManualCheckModal, setShowLicenceCheckManualCheckModal] = useState(false);
    const [licenceCheckManualCheckTooltip, setLicenceCheckManualCheckTooltip] = useState("Perform a manual licence check for this employee");

    const allRoutes = [
        { location: `${window.env.VISION_URL}/#/Drivers/RedirectToProfile?employeeId=${id}`, title: "Details", externalLink: true },
        { location: `${window.env.VISION_URL}/#/Drivers/RedirectToProfile?employeeId=${id}&tab=tab-performance`, title: "Performance", externalLink: true, requiredAction: "Performance:View" },
        { location: `${window.env.VISION_URL}/#/Drivers/RedirectToProfile?employeeId=${id}&tab=tab-debrief-archive`, title: "Debrief Archive", externalLink: true, requiredAction: "Debrief:View" },
        { location: `/employee/${id}/reviews`, title: "Reviews", requiredAction: "EmployeeReview:View" },
        { location: `/employee/${id}/licence-bureau`, title: "Licence Checking", requiredAction: licenceBureauAction },
        { location: `/employee/${id}/licence-checking`, title: "Licence Checking", requiredAction: "LicenceChecking:View" },
        { location: `${window.env.VISION_URL}/#/Drivers/RedirectToProfile?employeeId=${id}&tab=tab-training`, title: "Training", requiredAction: "Training:View", externalLink: true },
        { location: `/employee/${id}/assessments`, title: "Assessments", requiredAction: "EmployeeAssessments:View" },
        { location: `/employee/${id}/medical`, title: "Medical", requiredAction: "Employees:ViewSensitive" },
        { location: `/employee/${id}/collisions`, title: "Collisions", requiredAction: "Collisions:View" },
        { location: `/employee/${id}/checks`, title: "Checks", requiredAction: "Checks:View" },
        { location: `/employee/${id}/walkaround-checks`, title: "Walkaround Checks - Legacy", requiredAction: "WalkaroundCheck:View" },
        { location: `/employee/${id}/documents`, title: "Documents", requiredAction: "Documents:View" },
        { location: `${window.env.VISION_URL}/#/Drivers/RedirectToProfile?employeeId=${id}&tab=tab-audit-history`, title: "History", externalLink: true },
    ];
    const routesForUser = allRoutes.filter(x => x.requiredAction ? UserHasAccess(x.requiredAction) : true);

    useEffect(() => {
        if (!canAccessPage) {
            return;
        }

        setLoadingData(true);

        axios
            .all([
                EmployeeService.getEmployee(id),
                EmployeeService.getEmployeeOptions(),
            ])
            .then(
                axios.spread(function (
                    employeeResponse,
                    employeeOptionsResponse
                ) {
                    setEmployee(employeeResponse);
                    setEmployeeOptions(employeeOptionsResponse);
                    setLoadingData(false);
                }),
                () =>
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not load employee. Please try again."
                    )
            );

        if (canAccessLicenceBureau) {
            setLoadingLicenceBureau(true);
            AlertService.getAlertTypes(false, false).then(
                (response) => {
                    const newAlertTypes = response
                        .filter((x) => alertTypeKeys.includes(x.key))
                        .map((x) => {
                            return {
                                id: x.alertTypeId,
                                key: x.key,
                            };
                        });

                    setLicenceBureauAlertTypes(newAlertTypes);
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not load licence bureau alerts. Please try again."
                    );
                    setLoadingLicenceBureau(false);
                }
            );

            LicenceBureauService.getLicenceCheck(id).then(
                (response) => {
                    setLicenceCheck(response);
                },
                (error) => {
                    setLoadingLicenceBureau(false);
                    setLicenceCheckError(error.response.status);
                }
            );
        }

        if (canPerformLicenceCheckManualChecks) {
            LicenceCheckingService.getCredits().then(function (response) {
                dispatch({
                    type: "SET_LICENCE_CREDITS",
                    payload: response.licenceCredits,
                });

                if (response.licenceCredits <= 0) {
                    setLicenceCheckManualCheckTooltip(
                        "Check cannot be performed as you have no credits remaining"
                    );
                }
            });
        }
    }, [id]);

    useEffect(() => {
        if (licenceBureauAlertTypes.length > 0 && licenceCheck) {
            AlertService.getEntityBreakdown([id], licenceBureauAlertTypes.map(x => x.id)).then((response) => {
                const lowAlertCount = getCountForAlertType("licence-bureau-low", response);
                const highAlertCount = getCountForAlertType("licence-bureau-high", response);
                const criticalAlertCount = getCountForAlertType("licence-bureau-critical", response);

                const alertCounts = {
                    counts: [],
                    color: '00C6B2',
                    tooltip: null
                };

                if (criticalAlertCount > 0) {
                    alertCounts.counts.push(`${criticalAlertCount} critical alerts`);
                    alertCounts.color = 'EC2D6D';
                }

                if (highAlertCount > 0) {
                    alertCounts.color = alertCounts.counts.length === 0 ? 'FF7454' : alertCounts.color;
                    alertCounts.counts.push(`${highAlertCount} high alerts`);
                }

                if (lowAlertCount > 0) {
                    alertCounts.color = alertCounts.counts.length === 0 ? 'FFC107' : alertCounts.color;
                    alertCounts.counts.push(`${lowAlertCount} medium/low alerts`);
                }

                if (alertCounts.counts.length > 0) {
                    alertCounts.tooltip = <div>
                        <p>There are the following licence bureau alerts for this employee:</p>
                        <ul>
                            {alertCounts.counts.map(x => <li key={x}>- {x}</li>)}
                        </ul>
                    </div>;
                } else {
                    alertCounts.tooltip = <span>There are no licence bureau alerts for the employee.</span>;
                }

                setLicenceBureauAlertStatus(alertCounts);
                setLoadingLicenceBureau(false);
            }, () => {
                NotificationToaster.show(Intent.DANGER, "Could not load licence bureau alerts. Please try again.");
                setLoadingLicenceBureau(false);
            });
        } else if (licenceCheckError) {
            setLicenceBureauAlertStatus({
                color: '5C7080',
                tooltip: licenceCheckError === noLicenceCheckCode ?
                    <span>No matching employee record found in Licence Bureau.</span>
                    : <span>The licence checking service is currently unavailable. Please try again.</span>
            });
        }
    }, [licenceBureauAlertTypes, licenceCheck, licenceCheckError]);

    useEffect(() => {
        if (licenceCheck && !licenceLink) {
            LicenceBureauService.getUser(licenceCheck.organisationId)
                .then((user) => {
                    setLicenceLink(user.signInUrl);
                }, () => {
                    NotificationToaster.show(Intent.DANGER, "Unable to load the link to the licence checking provider. Please try again.");
                });
        }
    }, [licenceCheck])

    function getCountForAlertType(alertTypeKey, breakdown) {
        const alertTypeId = licenceBureauAlertTypes.find(x => x.key === alertTypeKey).id;

        return breakdown ? breakdown[0].alertTypeBreakdown.find(x => x.alertTypeId === alertTypeId).alertCount : 0;
    }

    function isAgencyEmployee() {
        return employee?.employeeLocation?.type?.name?.toLowerCase() === 'agency';
    }

    function getStatusText() {
        if (employee) {
            return isAgencyEmployee() ? `${employee.employeeLocation.status.name} - ${employee.employeeLocation.type.name}` : employee.employeeLocation.status.name;
        }
        return '';
    }

    function onLicenceCheckManualCheckSuccess() {
        if (location.pathname !== `/employee/${id}/licence-checking`) {
            navigate(`/employee/${id}/licence-checking`);
        }

        setLicenceCheckDate(new Date().toString());
    }

    return <UserAccess perform={requiredActions}
        yes={() => (
            <Fragment>
                <div className="row">

                    <Breadcrumbs items={props.breadcrumbs} />

                    <div className={classNames({ "bp6-skeleton": loadingData }, 'inline-items', 'space-large', 'spacer-bottom')}>
                        <h1>{employee?.fullName}</h1>
                        <StatusBlock
                            status={employee?.employeeLocation?.status?.name}
                            hollow={isAgencyEmployee()}
                            bold={false}
                        >
                            {getStatusText()}
                        </StatusBlock>
                        <ShowHide
                            evaluator={canAccessLicenceBureau}
                            show={(
                                <CircleStatus
                                    background={licenceBureauAlertStatus?.color}
                                    tooltip={licenceBureauAlertStatus?.tooltip}
                                    iconClass={"fa fa-list-alt"}
                                    loading={loadingLicenceBureau}
                                />
                            )}
                        />
                    </div>
                    <NotificationInline intent="danger" icon="mugshot" show={employee?.employeeLocation?.disqualified === true} text={(<span><strong>Driver Disqualified: </strong>{employee?.fullName} has been disqualified from driving.</span>)} />
                    <div className="button-row spacer-bottom">
                        <LinkButton
                            href={`${window.env.VISION_URL}/#/Drivers/RedirectToCalendar?driverId=${id}&`}
                            text={"Calendar"}
                            loading={loadingData}
                            icon={"calendar"}
                            externalLink={true}
                            intent={Intent.PRIMARY}
                        />
                        <UserAccess
                            perform={'Debrief:Perform'}
                            yes={() => <LinkButton
                                href={`${window.env.VISION_URL}/#/Drivers/RedirectToProfile?employeeId=${id}&openDebrief`}
                                text={"Debrief"}
                                loading={loadingData}
                                icon={"person"}
                                externalLink={true}
                                intent={Intent.PRIMARY}
                            />}
                        />
                        <ShowHide
                            evaluator={canPerformLicenceCheckManualChecks && employee?.employeeLocation?.validLicenceCheckingDeclaration}
                            show={
                                <Tooltip content={licenceCheckManualCheckTooltip}>
                                    <Button
                                        text={"Check"}
                                        loading={loadingData}
                                        icon={"mugshot"}
                                        intent={Intent.PRIMARY}
                                        onClick={() => { setShowLicenceCheckManualCheckModal(true) }}
                                        disabled={licenceCheckingCredits <= 0}
                                    />
                                </Tooltip>
                            }
                        />
                        <ShowHide
                            evaluator={canAccessLicenceBureau && licenceCheck}
                            show={<LinkButton
                                href={`${licenceLink}/users/${licenceCheck?.userId}`}
                                text={"Licence Bureau"}
                                loading={!licenceLink}
                                externalLink={true}
                                intent={Intent.PRIMARY}
                                openInNewTab={true}
                                icon="share"
                            />}
                        />
                    </div>
                </div>

                <div className="layout-cols">
                    <div className="col side-col">
                        <div className="row">
                            <VerticalLinkGroup exactRouteMatch={false} links={routesForUser} />
                        </div>
                    </div>
                    <div className="col content-col">
                        <Routes>
                            <Route path="Details" element={<LegacyRedirect id={id} />} />
                            <Route path="" element={<Navigate to={`/employee/${id}/licence-checking`} replace />} />
                            <Route path="licence-bureau" element={<EmployeeLicenceBureau
                                    employeeId={id}
                                    employeeOptions={employeeOptions}
                                    licenceBureauAlertTypes={licenceBureauAlertTypes}
                                    licenceCheck={licenceCheck}
                                    errorCode={licenceCheckError}
                                    loading={loadingLicenceBureau}
                                />} />
                            <Route path="licence-checking" element={<EmployeeLicenceChecking
                                    key={licenceCheckDate}
                                    employeeId={id}
                                    employeeInactive={employee?.employeeLocation?.status?.inactive}
                                    employeeName={employee?.fullName}
                                />} />
                            <Route path="checks" element={<ChecksListing
                                    employeeId={id}
                                    withBreadcrumbs={false}
                                    withCheckSettingsButton={false}
                                    withManageTemplatesButton={false}
                                    id={id}
                                    route={"employee"}
                                    category="Asset"
                                />} />
                            <Route path="documents/*" element={<Documents employeeId={id} />} />
                            <Route path="walkaround-checks" element={<DefectReporting
                                    id={id} route={"employee"} sortName={"assetRegistration"} headerName={"Vehicle Registration"}
                                />} />
                            <Route path="assessments" element={<EmployeeAssessments employeeId={id} />} />
                            <Route path="medical" element={<EmployeeMedical employeeId={id} />} />
                            <Route path="reviews" element={<EmployeeReviews key={id} employeeId={id} />} />
                            <Route path="collisions" element={<CollisionListing
                                    profileType="employee"
                                    entityId={id}
                                />} />
                        </Routes>
                    </div>
                </div>

                <LicenceCheckingManualCheckModal
                    isOpen={showLicenceCheckManualCheckModal}
                    onClose={() => { setShowLicenceCheckManualCheckModal(false) }}
                    credits={licenceCheckingCredits}
                    employeeName={employee?.fullName}
                    employeeId={id}
                    onCheckSuccess={onLicenceCheckManualCheckSuccess}
                />
            </Fragment>
        )}
        no={() => (
            <PageRestricted />
        )}
    />;
}